import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Sidebar } from "@/components/layout/Sidebar";
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import ContactsPage from "@/pages/ContactsPage";
import BusinessesPage from "@/pages/BusinessesPage";
import CampaignsPage from "@/pages/CampaignsPage";
import EmailBuilderPage from "@/pages/EmailBuilderPage";
import TemplatesPage from "@/pages/TemplatesPage";
import ListsPage from "@/pages/ListsPage";
import LeadCapturePage from "@/pages/LeadCapturePage";
import AdminPage from "@/pages/AdminPage";
import ReportsPage from "@/pages/ReportsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import SettingsPage from "@/pages/SettingsPage";
import UnsubscribePage from "@/pages/UnsubscribePage";

const ProtectedRoute = ({ children, minRole = 'agent' }) => {
  const { user, loading, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#B0B0B0]">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!hasRole(minRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const DashboardLayout = ({ children }) => (
  <div className="flex min-h-screen bg-[#0A0A0A]">
    <Sidebar />
    {children}
  </div>
);

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardLayout><DashboardPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/contacts" element={
        <ProtectedRoute><DashboardLayout><ContactsPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/businesses" element={
        <ProtectedRoute><DashboardLayout><BusinessesPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/campaigns" element={
        <ProtectedRoute><DashboardLayout><CampaignsPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/campaigns/:campaignId/edit" element={
        <ProtectedRoute><EmailBuilderPage /></ProtectedRoute>
      } />
      <Route path="/templates" element={
        <ProtectedRoute><DashboardLayout><TemplatesPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/lists" element={
        <ProtectedRoute minRole="admin"><DashboardLayout><ListsPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/lead-capture" element={
        <ProtectedRoute minRole="admin"><DashboardLayout><LeadCapturePage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute minRole="admin"><DashboardLayout><AdminPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute minRole="admin"><DashboardLayout><AnalyticsPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute minRole="super_admin"><DashboardLayout><ReportsPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute><DashboardLayout><SettingsPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
